import { useEffect, useRef, useState, type KeyboardEvent } from 'react';

/**
 * WAI-ARIA combobox-with-listbox keyboard model (T015 review B1).
 *
 * Shared by `ComunePicker` (profile, both epochs) and the `LocalitaPopup`
 * suggestion list (v2 lane — the legacy lane gets it for free, same DOM):
 * focus stays in the input, the highlight moves through `aria-activedescendant`,
 * `Enter` commits the highlighted option and `Escape` closes the list first
 * (letting the next Escape reach the enclosing popup). A text input must never
 * submit the surrounding form, so `Enter` is always swallowed.
 *
 * `Home`/`End` are deliberately untouched: they keep their native
 * "move the caret" behaviour, which is what an editable combobox expects.
 */
export interface ComboboxKeyboardOptions {
  /** Number of options currently offered (already filtered by the query). */
  count: number;
  /** DOM id of the `role="listbox"` element. */
  listId: string;
  /** Commit the option at `index` (the caller owns the resulting state). */
  onSelect: (index: number) => void;
}

export interface ComboboxKeyboard {
  /** Highlighted option index, `-1` = none. */
  activeIndex: number;
  /** Whether the listbox is actually showing (non-empty and not Escape-dismissed). */
  open: boolean;
  /** `aria-activedescendant` value — undefined while nothing is highlighted. */
  activeDescendant: string | undefined;
  /** Call from the input's `onChange`: re-opens a dismissed list, clears the highlight. */
  onQueryChange: () => void;
  /** Wire to the input's `onKeyDown`. */
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  /** Props for option `index` (stable id + pointer highlight, so pointer and keyboard agree). */
  optionProps: (index: number) => { id: string; onMouseEnter: () => void };
}

function optionIdOf(listId: string, index: number): string {
  return `${listId}-option-${index}`;
}

export function useComboboxKeyboard({
  count,
  listId,
  onSelect,
}: ComboboxKeyboardOptions): ComboboxKeyboard {
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dismissed, setDismissed] = useState(false);

  // The key handler fires after render, so it must see the latest callbacks.
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const countRef = useRef(count);
  countRef.current = count;

  // The option set shrank (new query): a stale highlight must never survive
  // into an `Enter` that would commit a different option.
  useEffect(() => {
    setActiveIndex((index) => (index >= count ? -1 : index));
  }, [count]);

  // Keep the highlighted option visible inside a scrollable list.
  useEffect(() => {
    if (activeIndex < 0) return;
    document.getElementById(optionIdOf(listId, activeIndex))?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, listId]);

  const open = !dismissed && count > 0;

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault(); // no caret movement, no page scroll
        setDismissed(false);
        if (countRef.current === 0) return;
        const step = event.key === 'ArrowDown' ? 1 : -1;
        setActiveIndex((index) => {
          if (index < 0) return step === 1 ? 0 : countRef.current - 1;
          const next = index + step;
          if (next < 0) return countRef.current - 1; // wrap
          if (next >= countRef.current) return 0;
          return next;
        });
        return;
      }
      case 'Enter': {
        // Never submit the surrounding form from a search field; commit the
        // highlighted option when there is one.
        event.preventDefault();
        const index = activeIndex;
        if (index >= 0 && index < count) {
          setActiveIndex(-1);
          setDismissed(true);
          onSelectRef.current(index);
        }
        return;
      }
      case 'Escape': {
        if (countRef.current > 0 && !dismissed) {
          // First Escape closes the LIST only; with nothing to close the
          // keystroke bubbles so the enclosing popup can close itself.
          event.preventDefault();
          event.stopPropagation();
          setDismissed(true);
          setActiveIndex(-1);
        }
        return;
      }
      default:
    }
  };

  const onQueryChange = () => {
    setDismissed(false);
    setActiveIndex(-1);
  };

  const optionProps = (index: number) => ({
    id: optionIdOf(listId, index),
    onMouseEnter: () => setActiveIndex(index),
  });

  return {
    activeIndex,
    open,
    activeDescendant: open && activeIndex >= 0 ? optionIdOf(listId, activeIndex) : undefined,
    onQueryChange,
    onKeyDown,
    optionProps,
  };
}
